import React, { ReactElement, useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, ActivityIndicator, Pressable, ScrollView,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import Style from '../../constants/Style';
import PlusIcon from '../../components/16pxIcons/PlusIcon';
import AlertModal from '../../components/AlertModal';
import BuildingMenuItem from './components/BuildingMenuItem';
import SearchPane from './components/SearchPane';
import BuildingTitleItem from './components/BuildingTitleItem';
import BuildingSettingsModal from './components/BuildingSettingsModal';
import BuildingFloorList from './components/BuildingFloorList';
import { Area, Floor, Space } from '../../components/Map/SetFloorsModal/FloorItem';
import FloorTitleItem from './components/BuildingFloorList/FloorTitleItem';
import AreaList from './components/BuildingFloorList/AreaList';
import AddBuildingModal, { Building } from './components/AddBuildingModal';
import Button from '../../components/Button';
import SetFloorsModal from '../../components/Map/SetFloorsModal';
import AreaTitleItem from './components/BuildingFloorList/AreaList/AreaTitleItem';
import SpaceList from './components/BuildingFloorList/AreaList/SpaceList';
import SpaceTitleItem from './components/BuildingFloorList/AreaList/SpaceList/SpaceTitleItem';
import NoBuildingImage from './components/NoBuildingImage';
import ScreenEmptyState from '../../components/ScreenEmptyState';
import { updateBuildingId } from '../../redux/selectedBuildingSlice';
import { logEvent } from '../../components/LogEvent';
import EditAreaModal from './components/BuildingFloorList/AreaList/EditAreaModal';
import EditSpaceModal from './components/BuildingFloorList/AreaList/SpaceList/EditSpaceModal';
import { updateLocations } from '../../redux/locationStructureSlice';
import PressableStyled from '../../components/PressableStyled';
import { getLocationHierarchyById } from '../../constants/Utils';
import { AppScreen } from '../../navigation/LinkingConfiguration';
import { UserPermission } from '../../constants/Types';
import { noPermissionsSubtitle, noPermissionsTitle } from '../../constants/AlertMessages';
import { RootTabScreenProps } from '../../types';
import GetEntireLocationStructure from '../../components/GetEntireLocationStructure';

const LocationManagementScreen = ({
  navigation, route,
}: RootTabScreenProps<AppScreen.LOCATION_MANAGEMENT>) => {
  const [expandedBuildingId, setExpandedBuildingId] = useState<string>(null);
  const [expandedFloorId, setExpandedFloorId] = useState<string>(null);
  const [expandedAreaId, setExpandedAreaId] = useState<string>(null);
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>(null);
  const [selectedFloorId, setSelectedFloorId] = useState<string>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<string>(null);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [buildingSettingsShown, setBuildingSettingsShown] = useState<boolean>(false);
  const [addBuildingModalShown, setAddBuildingModalShown] = useState<boolean>(false);
  const [addFloorModalShown, setAddFloorModalShown] = useState<boolean>(false);
  const [editFloorModalShown, setEditFloorModalShown] = useState<boolean>(false);
  const [editAreaModalShown, setEditAreaModalShown] = useState<boolean>(false);
  const [editSpaceModalShown, setEditSpaceModalShown] = useState<boolean>(false);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>(null);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>(null);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [buildingMenuShownId, setBuildingMenuShownId] = useState<string>(null);
  const buildings: { [key: string]: Building } = useSelector((state:
    { locations: { buildings: { [key: string]: Building } } }) => state.locations.buildings);
  const currentBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canViewLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_READ);
  const canAddLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_CREATE);
  const selectedBuilding: Building | null | undefined = buildings
    ? buildings[selectedBuildingId] : null;
  const selectedFloor: Floor = selectedBuilding?.floors[selectedFloorId];
  const selectedArea: Area = selectedFloor?.areas[selectedAreaId];
  const selectedSpace: Space = selectedArea?.spaces[selectedSpaceId];
  const [buildingsLoaderShown, setBuildingsLoaderShown] = useState<boolean>(true);
  const dispatch = useDispatch();

  const updateBuildings = (updatedBuildings) => {
    dispatch(updateLocations(structuredClone(updatedBuildings)));
  };

  useEffect(() => {
    const navigationLocationId: string = (route?.params as any)?.locationId;
    if (navigationLocationId) {
      const hierarchy = getLocationHierarchyById(navigationLocationId, buildings);
      setSelectedBuildingId(hierarchy.buildingId);
      setSelectedFloorId(hierarchy.floorId);
      setSelectedAreaId(hierarchy.areaId);
      setSelectedSpaceId(hierarchy.spaceId);
      navigation.setParams({ locationId: undefined } as any);
    }
  }, [route]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setBuildingsLoaderShown(false);
      clearTimeout(timeout);
    }, 1000);
    const unsubscribe = navigation.addListener('focus', () => {
      GetEntireLocationStructure((data) => dispatch(updateLocations(data)));
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!(buildings && currentBuildingId
      && !(selectedFloor || selectedArea || selectedSpace))) return;
    const defaultBuilding: Building = buildings[currentBuildingId];
    if (defaultBuilding) setSelectedBuildingId(defaultBuilding.LocationId);
  }, [currentBuildingId, buildings]);

  useEffect(() => {
    if (!((Object.keys(buildings ?? {}))?.length > 0)) return;
    if (selectedBuilding || selectedFloor || selectedArea || selectedSpace) return;
    const defaultBuilding: Building = buildings[currentBuildingId];
    if (defaultBuilding) setSelectedBuildingId(defaultBuilding.LocationId);
  }, [buildings]);

  useEffect(() => {
    setBuildingMenuShownId(null);
  }, [selectedBuilding, selectedFloor, searchTerm, expandedBuildingId, expandedFloorId,
    expandedAreaId]);

  useEffect(() => {
    setSearchTerm('');
  }, [selectedBuilding, selectedFloor, expandedBuildingId, expandedFloorId, expandedAreaId]);

  useEffect(() => setExpandedBuildingId(selectedBuildingId), [selectedBuildingId]);

  useEffect(() => {
    if (!selectedFloorId) return;
    setExpandedBuildingId(selectedFloor?.ParentId);
    setExpandedFloorId(selectedFloor?.LocationId);
    setExpandedAreaId(null);
    setSelectedBuildingId(selectedFloor?.ParentId);
  }, [selectedFloorId]);

  useEffect(() => {
    if (!selectedArea) return;
    let found: boolean = false;
    Object.keys(buildings ?? {}).forEach((buildingId: string) => {
      const building: Building | undefined = buildings[buildingId];
      Object.keys(building?.floors)?.forEach((floorId: string) => {
        if (!found && selectedArea.ParentId === floorId) {
          setExpandedBuildingId(building.LocationId);
          setExpandedFloorId(floorId);
          setExpandedAreaId(selectedArea.LocationId);
          setSelectedBuildingId(buildingId);
          setSelectedFloorId(floorId);
          found = true;
        }
      });
    });
  }, [selectedAreaId]);

  useEffect(() => {
    if (!selectedSpace) return;
    setExpandedAreaId(selectedSpace.ParentId);
    let found: boolean = false;
    Object.keys(buildings ?? {}).forEach((buildingId: string) => {
      const building: Building | undefined = buildings[buildingId];
      Object.keys(building?.floors ?? {}).forEach((floorId: string) => {
        const floor: Floor | undefined = building.floors[floorId];
        Object.keys(floor?.areas ?? {})?.forEach((areaId: string) => {
          const area: Area | undefined = floor.areas[areaId];
          if (!found && selectedSpace.ParentId === area.LocationId) {
            setExpandedBuildingId(buildingId);
            setExpandedFloorId(floorId);
            setExpandedAreaId(areaId);
            setSelectedBuildingId(buildingId);
            setSelectedFloorId(floorId);
            setSelectedAreaId(areaId);
            found = true;
          }
        });
      });
    });
  }, [selectedSpace]);

  const showScreenAlert = (
    title: string,
    message: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = null,
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setLoading(false);
    setAlertVisible(true);
  };

  const hideMenu: () => void = () => setBuildingMenuShownId(null);

  const removeBuilding = (id: string) => {
    updateBuildings(((buildingsObj: { [key: string]: Building }) => {
      const { [id]: _, ...rest } = buildingsObj;
      return rest;
    })(buildings));
    if (selectedBuildingId === id) {
      setSelectedBuildingId(null);
      setSelectedFloorId(null);
      setSelectedAreaId(null);
      setSelectedSpaceId(null);
    }
  };

  const addBuilding = (building: Building) => {
    updateBuildings({ ...buildings, [building.LocationId]: building });
  };

  const editBuilding = (newBuilding: Building) => {
    updateBuildings({
      ...buildings,
      [newBuilding.LocationId]: { ...buildings[newBuilding.LocationId], ...newBuilding },
    });
  };

  const openAddBuildingModal: () => void = () => {
    logEvent('LocationManagement', 'add_building');
    setAddBuildingModalShown(true);
    hideMenu();
  };

  const openAddFloorModal: () => void = () => {
    setAddFloorModalShown(true);
    setBuildingMenuShownId(null);
  };

  const navigateToAddArea: () => void = () => {
    setBuildingMenuShownId(null);
    const parentBuildingId: string = selectedFloor?.ParentId;
    if (parentBuildingId) dispatch(updateBuildingId(parentBuildingId));
    const floorId: string = selectedFloor?.LocationId;
    navigation.navigate(AppScreen.MAP as any, { floorId, edit_mode: true });
  };

  const navigateToAddSpace: () => void = () => {
    setBuildingMenuShownId(null);
    const parentBuildingId: string = Object.values(buildings ?? {})
      ?.filter((building: Building) => Object.values(building.floors ?? {})
        .filter((floor: Floor) => floor.LocationId
          === selectedArea?.ParentId).length === 1)[0].LocationId;
    if (parentBuildingId) dispatch(updateBuildingId(parentBuildingId));
    const floorId: string = selectedArea?.ParentId;
    const areaId: string = selectedArea?.LocationId;
    navigation.navigate(AppScreen.MAP as any, { floorId, areaId, edit_mode: true });
  };

  const showBuildingSettings: () => void = () => {
    logEvent('LocationManagement', 'show_building_settings');
    setBuildingSettingsShown(true);
  };

  const addFloor = (floor: Floor) => {
    const updatedBuildingData = structuredClone(buildings);
    updatedBuildingData[selectedBuildingId].floors[floor.LocationId] = { ...floor, areas: {} };
    updateBuildings(updatedBuildingData);
    setAddFloorModalShown(false);
  };

  const editFloor = (updatedFloor: Floor) => {
    const updatedBuildingData = structuredClone(buildings);
    const existingFloor: Floor = updatedBuildingData[selectedBuildingId]
      .floors[updatedFloor.LocationId];
    updatedBuildingData[selectedBuildingId].floors[updatedFloor.LocationId] = {
      ...existingFloor, ...updatedFloor,
    };
    updateBuildings(updatedBuildingData);
  };

  const removeFloor = (floorId: string, floorName: string) => {
    const updatedBuildingData = structuredClone(buildings);
    delete updatedBuildingData[selectedBuildingId].floors[floorId];
    updateBuildings(updatedBuildingData);
    showScreenAlert(
      'Floor successfully deleted',
      <Text>
        <Text style={Style.alertBoldText}>{floorName}</Text>
        {' has been deleted'}
        <Text style={Style.alertBoldText}>
          {selectedBuilding ? ` from ${selectedBuilding.Name}` : ''}
        </Text>
      </Text>,
      false,
    );
  };

  const removeArea = (areaId: string) => {
    const updatedBuildingData = structuredClone(buildings);
    delete updatedBuildingData[selectedBuildingId].floors[selectedFloorId].areas[areaId];
    updateBuildings(updatedBuildingData);
  };

  const updateArea = (updatedArea: Area) => {
    const updatedBuildingData = structuredClone(buildings);
    const { areas } = updatedBuildingData[selectedBuildingId].floors[selectedFloorId];
    const existingArea: Area = areas[updatedArea.LocationId];
    areas[updatedArea.LocationId] = { ...existingArea, ...updatedArea };
    updateBuildings(updatedBuildingData);
  };

  const updateSpace = (updatedSpace: Space) => {
    const updatedBuildingData = structuredClone(buildings);
    const { spaces } = updatedBuildingData[selectedBuildingId].floors[selectedFloorId]
      .areas[selectedAreaId];
    const spaceId: string = updatedSpace.LocationId;
    const existingSpace: Space = spaces[spaceId];
    spaces[spaceId] = { ...existingSpace, ...updatedSpace };
    updateBuildings(updatedBuildingData);
  };

  const removeSpace = (spaceId: string) => {
    const updatedBuildingData = structuredClone(buildings);
    const { spaces } = updatedBuildingData[selectedBuildingId].floors[selectedFloorId]
      .areas[selectedAreaId];
    delete spaces[spaceId];
    updateBuildings(updatedBuildingData);
  };

  const hideBuildingSettingsModal: () => void = () => {
    logEvent('LocationManagement', 'close_building_settings_modal');
    setBuildingSettingsShown(false);
  };

  const hideAddBuildingModal: () => void = () => setAddBuildingModalShown(false);

  const hideAddFloorModal: () => void = () => {
    logEvent('LocationManagement', 'close_add_floor_modal');
    setAddFloorModalShown(false);
  };

  const hideEditAreaModal: () => void = () => setEditAreaModalShown(false);

  const showEditAreaModal: () => void = () => setEditAreaModalShown(true);

  const hideEditSpaceModal: () => void = () => setEditSpaceModalShown(false);

  const showEditSpaceModal: () => void = () => setEditSpaceModalShown(true);

  const checkIfNameUnique = (name: string, id?: string) => !Object.values(buildings ?? {})
    .filter((building: Building) => !!building && building.LocationId !== id)
    .map((building: Building) => building.Name?.trim()?.toLowerCase())
    .includes(name?.trim().toLowerCase());

  return canViewLocations ? <Pressable
    onPress={hideMenu}
    style={{ flex: 1, cursor: 'auto' }}
  >
    <View style={styles.container}>
      <Text style={styles.title}>{'Location management'}</Text>
      <View style={[Style.row, styles.mainContent]}>
        <View style={styles.menuSection}>
          <View style={[Style.row, styles.headerSection]}>
            <Text style={styles.buildingsTitle}>{'BUILDINGS'}</Text>
            {canAddLocations && <PressableStyled
              style={styles.addBuildingIcon}
              onPress={openAddBuildingModal}
              testID='lm-add-building-btn'
            >
              <PlusIcon color={Colors.light.hereworksBlue500} />
            </PressableStyled>}
          </View>
          <ScrollView
            style={styles.menu}
            testID='building-list'
          >
            {!buildingsLoaderShown && Object.keys(buildings ?? {})?.slice()
              .sort((prev: string, next: string) => buildings[prev]
                ?.Name?.toLowerCase()?.localeCompare(buildings[next]?.Name?.toLowerCase()))
              ?.map((buildingId: string, index: number) => <BuildingMenuItem
                {...{
                  expandedBuildingId,
                  expandedFloorId,
                  expandedAreaId,
                  setExpandedBuildingId,
                  setExpandedFloorId,
                  setExpandedAreaId,
                  selectedBuilding,
                  selectedFloor,
                  selectedArea,
                  selectedSpace,
                  setSelectedBuildingId,
                  setSelectedFloorId,
                  setSelectedAreaId,
                  setSelectedSpaceId,
                  buildingMenuShownId,
                  setBuildingMenuShownId,
                  showScreenAlert,
                  setLoading,
                  index,
                  removeBuilding,
                  editBuilding,
                  checkIfNameUnique,
                }}
                key={`building_menu_item_${buildingId}`}
                testID={`building#${index + 1}`}
                building={buildings[buildingId]}
              />)}
            {buildings && Object.keys(buildings)?.length === 0
              && <Text style={styles.listEmptyTitle}>
                {'Please create a building'}
              </Text>}
            {!buildings && !loading && <Text style={styles.listEmptyTitle}>
              {'Unable to load list of buildings'}
            </Text>}
          </ScrollView>
        </View>
        <View style={styles.contentSection}>
          <View style={styles.headerSection}>
            <SearchPane
              {...{
                searchTerm,
                setSearchTerm,
                setSelectedFloorId,
                setSelectedAreaId,
                setSelectedSpaceId,
                selectedFloor,
                selectedArea,
                selectedSpace,
              }}
            />
            {canAddLocations && !selectedSpace && !selectedArea && selectedFloor && <Button
              affirmative
              title='Add Area'
              children={<PlusIcon style={styles.plusIcon} />}
              onPress={navigateToAddArea}
              testID='add-area-btn'
            />}
            {canAddLocations && !selectedSpace && !selectedArea && !selectedFloor
              && selectedBuilding
              && <Button
                affirmative
                title='Add Floor'
                children={<PlusIcon style={styles.plusIcon} />}
                onPress={openAddFloorModal}
                testID='add-floor-btn'
              />}
          </View>
          {buildingsLoaderShown ? <ActivityIndicator
            style={Style.activityIndicator}
            size='large'
          /> : <View style={styles.buildingDetailsContainer}>
            {selectedSpace && <SpaceTitleItem
              {...{ ...selectedSpace }}
              floorName={selectedArea?.ParentId}
              areaName={selectedArea?.Name}
              buildingId={expandedBuildingId}
              edit={showEditSpaceModal}
              clear={() => setSelectedSpaceId(null)}
            />}
            {!selectedSpace && selectedArea && <>
              <AreaTitleItem
                {...{ ...selectedArea }}
                edit={showEditAreaModal}
                clear={() => setSelectedAreaId(null)}
                buildingId={selectedFloor.ParentId}
              />
              <SpaceList
                spaces={Object.values(selectedArea?.spaces ?? {})}
                parentName={selectedArea.Name}
                parentId={selectedArea.ParentId}
                buildingId={Object.values(buildings ?? {})
                  .filter((building: Building) => Object.values(building?.floors ?? {})
                    ?.filter((floor: Floor) => floor.LocationId
                      === selectedArea.ParentId).length > 0)[0]?.LocationId}
                {...{
                  setSelectedSpaceId,
                  showScreenAlert,
                  updateSpace,
                  removeSpace,
                  navigateToAddSpace,
                }}
              />
            </>}
            {!selectedSpace && !selectedArea && selectedFloor && <>
              <FloorTitleItem
                {...{ ...selectedFloor }}
                edit={() => setEditFloorModalShown(true)}
                clear={() => setSelectedFloorId(null)}
              />
              <AreaList
                areas={Object.values(selectedFloor?.areas ?? {})}
                parentName={selectedFloor?.Name}
                {...{
                  selectedArea,
                  setSelectedAreaId,
                  removeArea,
                  updateArea,
                  showScreenAlert,
                  selectedFloor,
                  navigateToAddArea,
                }}
              />
            </>}
            {!selectedSpace && !selectedArea && !selectedFloor && selectedBuilding && <>
              <BuildingTitleItem {...{ selectedBuilding, showBuildingSettings }} />
              <BuildingFloorList
                {...{
                  editFloor,
                  removeFloor,
                  setSelectedFloorId,
                  selectedBuilding,
                  openAddFloorModal,
                }}
              />
            </>}
            {!loading && !selectedSpace && !selectedArea && !selectedFloor && !selectedBuilding
              && <ScreenEmptyState
                title={buildings ? 'No building yet' : 'Unable to load list of buildings'}
                image={<NoBuildingImage />}
                adminSubtitle={buildings ? 'Add a building you want to work with'
                  : 'Refresh the page to check again'}
                subtitle={buildings
                  ? 'Contact your system administrator to add a building you want to work with'
                  : 'Refresh the page to check again'}
                buttonTitle='Add building'
                buttonAction={openAddBuildingModal}
                actionAvailable={canAddLocations && !!buildings}
              />}
          </View>}
        </View>
      </View>
      <AlertModal
        title={alertTitle}
        message={alertMessage}
        error={alertError}
        modalVisible={alertVisible}
        setModalVisible={setAlertVisible}
        action={alertAction}
        actionTitle={alertActionTitle}
      />
      <ActivityIndicator
        style={Style.activityIndicator}
        size='large'
        animating={loading}
      />
      <BuildingSettingsModal
        {...{
          hideBuildingSettingsModal,
          selectedBuilding,
          buildingSettingsShown,
          editBuilding,
          showScreenAlert,
        }}
      />
      <AddBuildingModal
        {...{
          addBuilding,
          addBuildingModalShown,
          showScreenAlert,
          hideAddBuildingModal,
          checkIfNameUnique,
        }}
      />
      <SetFloorsModal
        isVisible={addFloorModalShown}
        hide={hideAddFloorModal}
        ParentId={selectedBuildingId}
        floors={Object.values(selectedBuilding?.floors ?? {})}
        {...{ addFloor }}
        testID='lm-close-add-floor-menu-btn'
      />
      <SetFloorsModal
        {...{ editFloor }}
        floors={Object.values(selectedBuilding?.floors ?? {})}
        deleteFloorToUpdate={() => removeFloor(selectedFloor?.LocationId, selectedFloor?.Name)}
        isVisible={editFloorModalShown}
        hide={() => setEditFloorModalShown(false)}
        floorToUpdate={selectedFloor}
        ParentId={selectedFloor?.ParentId}
      />
      <EditAreaModal
        {...{
          updateArea,
          showScreenAlert,
          removeArea,
          editAreaModalShown,
          hideEditAreaModal,
        }}
        area={selectedArea}
        parentName={selectedFloor?.Name}
      />
      <EditSpaceModal
        {...{
          removeSpace,
          updateSpace,
          showScreenAlert,
          editSpaceModalShown,
          hideEditSpaceModal,
        }}
        space={selectedSpace}
        parentName={selectedArea?.Name}
      />
    </View>
  </Pressable> : <ScreenEmptyState
    title={noPermissionsTitle('locations')}
    subtitle={noPermissionsSubtitle('locations')}
    large
  />;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 40,
    paddingTop: 48,
    backgroundColor: Colors.light.hereworksBackground,
  },
  title: {
    fontSize: 32,
    lineHeight: 48,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginBottom: 32,
  },
  mainContent: {
    flex: 1,
    backgroundColor: '#FFF',
    borderRadius: 12,
  },
  menuSection: {
    alignSelf: 'stretch',
    borderEndWidth: 1,
    borderEndColor: Colors.light.hereworksBlue100,
    minWidth: 240,
  },
  headerSection: {
    paddingStart: 24,
    paddingEnd: 18,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue100,
    height: 61,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 2,
  },
  buildingsTitle: {
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack500,
  },
  addBuildingIcon: {
    width: 32,
    aspectRatio: 1,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    justifyContent: 'center',
    alignItems: 'center',
    shadowRadius: 4,
    elevation: 2,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowColor: '#323247',
  },
  menu: {
    flex: 1,
    paddingVertical: 8,
  },
  listEmptyTitle: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack500,
    paddingVertical: 16,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginHorizontal: 12,
    backgroundColor: Colors.light.hereworksWhite100,
  },
  contentSection: {
    flex: 1,
    alignSelf: 'stretch',
  },
  buildingDetailsContainer: {
    marginVertical: 24,
    flex: 1,
  },
  plusIcon: {
    marginStart: 8,
  },
});

export default LocationManagementScreen;
